import html2canvas from 'html2canvas';

/**
 * html2canvas 1.4.x wirft bei modernen CSS-Farben (color-mix, oklch, …).
 * Clone bereinigen, dann Viewport-Screenshot und auf die Markierung zuschneiden
 * (WYSIWYG: genau das, was auf dem Bildschirm liegt).
 */

const UNSUPPORTED_COLOR_FN =
  /(?:color-mix|oklch|oklab|lab|lch|color|light-dark)\((?:[^()]|\([^()]*\))*\)/gi;

export const sanitizeCssForHtml2Canvas = (css: string): string =>
  css.replace(UNSUPPORTED_COLOR_FN, 'transparent');

const sanitizeCloneDocument = (doc: Document) => {
  doc.querySelectorAll('style').forEach((node) => {
    const text = node.textContent;
    if (text) node.textContent = sanitizeCssForHtml2Canvas(text);
  });

  doc.querySelectorAll('[style]').forEach((el) => {
    const style = el.getAttribute('style');
    if (!style) return;
    const cleaned = sanitizeCssForHtml2Canvas(style);
    if (cleaned !== style) el.setAttribute('style', cleaned);
  });
};

const shouldIgnoreCaptureElement = (el: Element): boolean => {
  if (!(el instanceof HTMLElement)) return false;
  if (el.dataset?.peCaptureIgnore === 'true') return true;
  const cls = el.classList;
  return (
    cls.contains('no-screenshot') ||
    cls.contains('print-hidden') ||
    cls.contains('pe-productivity-dock') ||
    el.id === 'pe-temp-portrait-print'
  );
};

export type CaptureViewportRegionOptions = {
  /** Viewport-Koordinaten (clientX/Y) */
  x: number;
  y: number;
  width: number;
  height: number;
  scale?: number;
  backgroundColor?: string | null;
};

/**
 * Erfasst genau den markierten Bildschirm-Ausschnitt.
 */
export async function captureViewportRegion(
  options: CaptureViewportRegionOptions
): Promise<HTMLCanvasElement> {
  const {
    x,
    y,
    width,
    height,
    scale = 1,
    backgroundColor = '#08090b',
  } = options;

  const selX = Math.max(0, Math.round(x));
  const selY = Math.max(0, Math.round(y));
  const selW = Math.max(1, Math.round(width));
  const selH = Math.max(1, Math.round(height));
  const captureScale = Math.max(1, scale);

  const vpW = window.innerWidth;
  const vpH = window.innerHeight;

  // Sichtbaren Viewport rendern — scrollX/Y negativ = aktuelle Bildschirmaussicht
  // (kein zusätzliches x/y mit scroll, sonst landet der Zuschnitt versetzt)
  const viewportCanvas = await html2canvas(document.documentElement, {
    backgroundColor,
    scale: captureScale,
    useCORS: true,
    logging: false,
    allowTaint: true,
    imageTimeout: 15000,
    width: vpW,
    height: vpH,
    windowWidth: vpW,
    windowHeight: vpH,
    scrollX: -window.scrollX,
    scrollY: -window.scrollY,
    ignoreElements: shouldIgnoreCaptureElement,
    onclone: (clonedDoc) => {
      sanitizeCloneDocument(clonedDoc);
    },
  });

  const crop = document.createElement('canvas');
  crop.width = Math.max(1, Math.round(selW * captureScale));
  crop.height = Math.max(1, Math.round(selH * captureScale));
  const ctx = crop.getContext('2d');
  if (!ctx) throw new Error('Canvas-Kontext nicht verfügbar');

  if (backgroundColor) {
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, crop.width, crop.height);
  }

  const srcX = Math.min(viewportCanvas.width, Math.round(selX * captureScale));
  const srcY = Math.min(viewportCanvas.height, Math.round(selY * captureScale));
  const srcW = Math.min(crop.width, viewportCanvas.width - srcX);
  const srcH = Math.min(crop.height, viewportCanvas.height - srcY);

  if (srcW <= 0 || srcH <= 0) {
    throw new Error('Markierung liegt außerhalb des sichtbaren Bereichs');
  }

  ctx.drawImage(viewportCanvas, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
  return crop;
}

/**
 * Dark-UI-Screenshot → druckfreundlich (heller Grund, dunkle Schrift).
 * invert + hue-rotate hält Farben lesbar, ohne Graustufen-Auswaschen.
 */
export function toPrintFriendlyCanvas(
  source: HTMLCanvasElement,
  isDark: boolean
): HTMLCanvasElement {
  if (!isDark) return source;
  const out = document.createElement('canvas');
  out.width = source.width;
  out.height = source.height;
  const ctx = out.getContext('2d');
  if (!ctx) return source;
  ctx.filter = 'invert(1) hue-rotate(180deg)';
  ctx.drawImage(source, 0, 0);
  ctx.filter = 'none';
  return out;
}
